import { apiClient } from './client';

export interface AdminInboundEmailLogRow {
  id: string;
  messageId: string | null;
  fromEmail: string;
  toEmail: string;
  subject: string;
  status: string;
  ticketId: string | null;
  ticketNumber: string | null;
  companyId: string | null;
  companyName: string | null;
  error: string | null;
  createdAt: string;
  canReprocess?: boolean;
}

export const adminApi = {
  // ── Stats ─────────────────────────────────────────────────
  getStats: async () => {
    const res = await apiClient.get('/admin/stats');
    return res.data.data as { totalCompanies: number; totalUsers: number; totalTickets: number; totalTimeHours: number };
  },

  // ── Companies ─────────────────────────────────────────────
  getCompanies: async (params: { page?: number; limit?: number; search?: string } = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && searchParams.set(k, String(v)));
    const res = await apiClient.get(`/admin/companies?${searchParams}`);
    return res.data.data as { companies: any[]; total: number; page: number; limit: number };
  },

  createCompany: async (data: { name: string; ownerEmail: string }) => {
    const res = await apiClient.post('/admin/companies', data);
    return res.data.data.company;
  },

  updateCompany: async (companyId: string, data: { name?: string; isActive?: boolean }) => {
    const res = await apiClient.patch(`/admin/companies/${companyId}`, data);
    return res.data.data.company;
  },

  updateCompanyStatus: async (companyId: string, isActive: boolean) => {
    const res = await apiClient.patch(`/admin/companies/${companyId}/status`, { isActive });
    return res.data.data.company;
  },

  getCompanyMembers: async (companyId: string) => {
    const res = await apiClient.get(`/admin/companies/${companyId}/members`);
    return res.data.data.members as any[];
  },

  addCompanyMember: async (companyId: string, data: { userEmail: string; role: string }) => {
    const res = await apiClient.post(`/admin/companies/${companyId}/members`, data);
    return res.data.data.member;
  },

  updateMemberRole: async (companyId: string, userId: string, role: string) => {
    const res = await apiClient.patch(`/admin/companies/${companyId}/members/${userId}`, { role });
    return res.data.data.member;
  },

  removeCompanyMember: async (companyId: string, userId: string) => {
    await apiClient.delete(`/admin/companies/${companyId}/members/${userId}`);
  },

  // ── Users ─────────────────────────────────────────────────
  getUsers: async (params: { page?: number; limit?: number; search?: string } = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && searchParams.set(k, String(v)));
    const res = await apiClient.get(`/admin/users?${searchParams}`);
    return res.data.data as { users: any[]; total: number; page: number; limit: number };
  },

  createUser: async (data: { name: string; email: string; password: string; isSuperAdmin?: boolean }) => {
    const res = await apiClient.post('/admin/users', data);
    return res.data.data.user;
  },

  updateUser: async (userId: string, data: { name?: string; isSuperAdmin?: boolean }) => {
    const res = await apiClient.patch(`/admin/users/${userId}`, data);
    return res.data.data.user;
  },

  deleteUser: async (userId: string) => {
    await apiClient.delete(`/admin/users/${userId}`);
  },

  getUserCompanies: async (userId: string) => {
    const res = await apiClient.get(`/admin/users/${userId}/companies`);
    return res.data.data.memberships as any[];
  },

  // ── Inbound email log (all tenants) ───────────────────────
  getInboundEmailLogs: async (params: { page?: number; limit?: number } = {}) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && searchParams.set(k, String(v)));
    const res = await apiClient.get(`/admin/inbound-email-logs?${searchParams}`);
    return res.data.data as { logs: AdminInboundEmailLogRow[]; total: number; page: number; limit: number };
  },

  reprocessInboundEmailLog: async (id: string) => {
    const res = await apiClient.post(`/admin/inbound-email-logs/${id}/reprocess`);
    return res.data.data.result as { ok: boolean; status: string; error?: string | null; ticketNumber?: string | null };
  },

  seedDemoData: async (companyId: string) => {
    const res = await apiClient.post(`/admin/companies/${companyId}/seed-demo`);
    return res.data.data as { users: number; categories: number; tickets: number; articles: number; crmOrgs: number; crmContacts: number; deals: number };
  },
};
